import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Save,
    User,
    Shield,
    Mail,
    Phone,
    Hash,
    MapPin,
    Trash2,
    Plus,
    AlertCircle,
    Database,
    UserCheck,
    Crown,
    Headphones,
    Code,
    UserX
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useTickets } from '../../contexts/TicketContext';

const AdminUserEditor = ({ user: targetUser, onClose, onSave }) => {
    const { adminFullUpdateUser, deleteUser } = useAuth();
    const { deleteAllTicketsFromUser } = useTickets();
    const [formData, setFormData] = useState({ ...targetUser });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (targetUser) {
            setFormData({ ...targetUser });
        }
    }, [targetUser]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCityChange = (index, field, value) => {
        const newCities = [...(formData.cities || [])];
        newCities[index] = { ...newCities[index], [field]: value };
        setFormData(prev => ({ ...prev, cities: newCities }));
    };

    const addCity = () => {
        setFormData(prev => ({
            ...prev,
            cities: [...(prev.cities || []), { city: 'CAPITAL', playerId: '', characterName: '' }]
        }));
    };

    const removeCity = (index) => {
        setFormData(prev => ({
            ...prev,
            cities: prev.cities.filter((_, i) => i !== index)
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await adminFullUpdateUser(targetUser.discordId, formData);
            if (result.success) {
                toast.success('Usuário atualizado com sucesso!');
                onSave && onSave(result.user);
                onClose();
            } else {
                setError(result.error);
                toast.error(result.error);
            }
        } catch (err) {
            setError('Erro ao salvar as alterações');
            toast.error('Erro ao salvar as alterações');
        } finally {
            setLoading(false);
        }
    };

    if (!targetUser) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm overflow-y-auto"
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-gray-950 border border-white/10 w-full max-w-4xl rounded-3xl shadow-2xl flex flex-col max-h-[90vh]"
            >
                {/* Header */}
                <div className="p-6 border-b border-white/5 flex items-center justify-between sticky top-0 bg-gray-950 z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <User className="h-6 w-6 text-blue-400" />
                            Editar Perfil: {targetUser.name}
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">
                            Alteração completa de privilégios e dados cadastrais
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className="text-gray-500 hover:text-white hover:bg-white/5 rounded-full"
                    >
                        <X className="h-6 w-6" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                    {error && (
                        <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Erro</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Seção 1: Informações Básicas */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Mail className="h-5 w-5 text-gray-500" />
                            Informações Básicas
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Nome Completo</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <Input
                                        value={formData.name || ''}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className="pl-10 bg-white/5 border-white/10 text-white"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Discord ID</label>
                                <div className="relative">
                                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <Input
                                        value={formData.discordId || ''}
                                        disabled
                                        className="pl-10 bg-white/5 border-white/10 text-gray-500 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <Input
                                        value={formData.email || ''}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className="pl-10 bg-white/5 border-white/10 text-white"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">WhatsApp / Telefone</label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <Input
                                        value={formData.phone || ''}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className="pl-10 bg-white/5 border-white/10 text-white"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Seção 2: Acesso e Status */}
                    <div className="space-y-4 pt-4 border-t border-white/5">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Shield className="h-5 w-5 text-gray-500" />
                            Nível de Acesso e Conta
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Cargo / Hierarquia</label>
                                <Select
                                    value={formData.accessLevel}
                                    onValueChange={(val) => handleInputChange('accessLevel', val)}
                                >
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-12 rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-900 border-white/10 text-white z-[200]">
                                        <SelectItem value="user">Usuário Comum</SelectItem>
                                        <SelectItem value="suporte">Suporte Nível 1</SelectItem>
                                        <SelectItem value="suporte_compras">Suporte Compras</SelectItem>
                                        <SelectItem value="moderador">Moderador</SelectItem>
                                        <SelectItem value="super_moderador">Super Moderador</SelectItem>
                                        <SelectItem value="programador">Programador</SelectItem>
                                        <SelectItem value="head_suporte">Head de Suporte</SelectItem>
                                        <SelectItem value="head_moderacao">Head de Moderação</SelectItem>
                                        <SelectItem value="head_staff">Head Staff</SelectItem>
                                        <SelectItem value="analista_qualidade">Analista de Qualidade</SelectItem>
                                        <SelectItem value="departamento_juridico">Departamento Jurídico</SelectItem>
                                        <SelectItem value="criador_de_conteudo">Criador de Conteúdo</SelectItem>
                                        <SelectItem value="admin">Administrador</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-500 uppercase">Status de Registro</label>
                                <Select
                                    value={formData.registrationStatus}
                                    onValueChange={(val) => handleInputChange('registrationStatus', val)}
                                >
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-12 rounded-xl">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-900 border-white/10 text-white z-[200]">
                                        <SelectItem value="registered">Cadastrado / Ativo</SelectItem>
                                        <SelectItem value="pre_registered">Pré-Cadastro / Pendente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Seção 3: Personagens / Cidades */}
                    <div className="space-y-4 pt-4 border-t border-white/5">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-gray-500" />
                                Personagens por Cidade
                            </h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addCity}
                                className="bg-blue-500/10 border-blue-500/20 text-blue-400 hover:bg-blue-500/20"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Adicionar Cidade
                            </Button>
                        </div>

                        <div className="space-y-3">
                            {(formData.cities || []).map((cityData, index) => (
                                <div
                                    key={index}
                                    className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-white/5 rounded-2xl border border-white/5 relative group"
                                >
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Cidade</label>
                                        <Select
                                            value={cityData.city}
                                            onValueChange={(val) => handleCityChange(index, 'city', val)}
                                        >
                                            <SelectTrigger className="bg-black/20 border-white/10 text-white h-10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-gray-900 border-white/10 text-white z-[200]">
                                                <SelectItem value="CAPITAL">CAPITAL</SelectItem>
                                                <SelectItem value="VALLEY NORTE">VALLEY NORTE</SelectItem>
                                                <SelectItem value="VALLEY SUL">VALLEY SUL</SelectItem>
                                                <SelectItem value="MORADA DO VALLEY">MORADA DO VALLEY</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Player ID</label>
                                        <Input
                                            value={cityData.playerId}
                                            onChange={(e) => handleCityChange(index, 'playerId', e.target.value)}
                                            placeholder="Ex: 12345"
                                            className="bg-black/20 border-white/10 text-white h-10"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Nickname</label>
                                        <Input
                                            value={cityData.characterName}
                                            onChange={(e) => handleCityChange(index, 'characterName', e.target.value)}
                                            placeholder="Ex: Fulano Silva"
                                            className="bg-black/20 border-white/10 text-white h-10"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Status</label>
                                        <Select
                                            value={cityData.status || 'verified'}
                                            onValueChange={(val) => handleCityChange(index, 'status', val)}
                                        >
                                            <SelectTrigger className={`h-10 border-white/10 text-white ${cityData.status === 'pending_approval' ? 'bg-orange-500/20 text-orange-200' : 'bg-black/20'}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-gray-900 border-white/10 text-white z-[200]">
                                                <SelectItem value="verified">Verificado</SelectItem>
                                                <SelectItem value="pending_approval">Pendente Validação</SelectItem>
                                                <SelectItem value="pending">Pendente</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-end justify-end">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeCity(index)}
                                            className="text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-xl"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {(!formData.cities || formData.cities.length === 0) && (
                                <div className="text-center py-8 bg-white/5 rounded-2xl border border-dashed border-white/10 text-gray-500 italic">
                                    Nenhum personagem vinculado.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Seção 4: Dados Congelados / Auditoria */}
                    <div className="space-y-4 pt-4 border-t border-white/5">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Database className="h-5 w-5 text-gray-500" />
                            Auditoria e Dados Congelados
                        </h3>
                        <Card className="bg-red-500/5 border-red-500/10 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase">ID Original (Frozen)</label>
                                    <Input
                                        value={formData.frozenData?.id || ''}
                                        onChange={(e) => handleInputChange('frozenData', { ...formData.frozenData, id: e.target.value })}
                                        className="bg-black/40 border-white/10 text-gray-300 font-mono text-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase">Discord ID Original</label>
                                    <Input
                                        value={formData.frozenData?.discordId || ''}
                                        onChange={(e) => handleInputChange('frozenData', { ...formData.frozenData, discordId: e.target.value })}
                                        className="bg-black/40 border-white/10 text-gray-300 font-mono text-sm"
                                    />
                                </div>
                            </div>
                        </Card>

                        {/* Seção 5: Zona de Perigo */}
                        <div className="space-y-4 pt-4 border-t border-red-500/20">
                            <h3 className="text-lg font-bold text-red-500 flex items-center gap-2">
                                <AlertCircle className="h-5 w-5" />
                                Zona de Perigo
                            </h3>
                            <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-bold text-red-400">Excluir Todos os Tickets</h4>
                                        <p className="text-sm text-red-400/70">
                                            Esta ação removerá permanentemente todos os tickets criados por este usuário.
                                            Esta ação não pode ser desfeita.
                                        </p>
                                    </div>
                                    <Button
                                        variant="destructive"
                                        onClick={async () => {
                                            if (confirm(`ATENÇÃO: Você está prestes a EXCLUIR TODOS OS TICKETS de ${targetUser.name}.\n\nEsta ação é IRREVERSÍVEL.\n\nTem certeza que deseja continuar?`)) {
                                                setLoading(true);
                                                try {
                                                    const result = await deleteAllTicketsFromUser(targetUser.id, targetUser.discordId);
                                                    if (result.success) {
                                                        toast.success(`${result.deletedCount} tickets excluídos com sucesso!`);
                                                        onSave && onSave(targetUser); // Refresh parent view
                                                    } else {
                                                        toast.error('Erro ao excluir tickets: ' + result.error);
                                                    }
                                                } catch (error) {
                                                    toast.error('Erro ao processar solicitação');
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }
                                        }}
                                        className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/50"
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Excluir Tickets
                                    </Button>
                                </div>
                            </div>

                            <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-bold text-red-400">EXCLUIR USUÁRIO DO SISTEMA</h4>
                                        <p className="text-sm text-red-400/70">
                                            Remove TODO o registro, histórico, tickets e dados vinculados.
                                            O usuário será tratado como NOVO se entrar novamente.
                                        </p>
                                    </div>
                                    <Button
                                        variant="destructive"
                                        onClick={async () => {
                                            const confirm1 = confirm(`PERIGO: Você está prestes a EXCLUIR COMPLETAMENTE o usuário ${targetUser.name}.\n\nIsso apagará:\n- Dados de Login\n- Tickets\n- Histórico de Auditoria\n- Punições\n\nCONTINUAR?`);
                                            if (confirm1) {
                                                const confirm2 = confirm(`Tem certeza ABSOLUTA? Esta ação NÃO pode ser desfeita.`);
                                                if (confirm2) {
                                                    setLoading(true);
                                                    try {
                                                        // 1. Delete Tickets first
                                                        await deleteAllTicketsFromUser(targetUser.id, targetUser.discordId);

                                                        // 2. Delete User Data
                                                        const result = await deleteUser(targetUser.discordId);

                                                        if (result.success) {
                                                            toast.success('Usuário excluído com sucesso!');
                                                            onSave && onSave(null); // Signal deletion to parent
                                                            onClose();
                                                        } else {
                                                            toast.error(result.error);
                                                        }
                                                    } catch (error) {
                                                        console.error(error);
                                                        toast.error('Erro fatal ao excluir usuário');
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }
                                            }
                                        }}
                                        className="bg-red-600 hover:bg-red-700 text-white border-2 border-red-800 shadow-lg shadow-red-900/50"
                                    >
                                        <UserX className="h-4 w-4 mr-2" />
                                        EXCLUIR TUDO
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-white/5 bg-gray-950 rounded-b-3xl sticky bottom-0 z-10">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <AlertCircle className="h-4 w-4" />
                            Alterações aplicadas instantaneamente e registradas.
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="text-gray-400 hover:text-white"
                            >
                                Cancelar
                            </Button>
                            <Button
                                disabled={loading}
                                onClick={handleSave}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 h-12 rounded-2xl font-bold shadow-lg shadow-blue-500/20 w-full md:w-auto"
                            >
                                {loading ? (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                                        className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full"
                                    />
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Save className="h-5 w-5" />
                                        Salvar Alterações
                                    </div>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div >
    );
};

export default AdminUserEditor;
